import { Component, inject, Input, Output, EventEmitter, ChangeDetectionStrategy, LOCALE_ID } from '@angular/core'
import { formatDate } from '@angular/common'
import { MatIcon } from '@angular/material/icon'
import { Meeting, MeetingSessionStatus, MeetingsService, MeetingStatus, MeetingWithLatestSession } from '../meetings.service'
import { MatDialog } from '@angular/material/dialog'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatTooltipModule } from '@angular/material/tooltip'
import { RouterLink } from '@angular/router'
import { MatIconButton } from '@angular/material/button'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { addHours, subMinutes } from 'date-fns'
import { formatTimeUntil, openExternalUrl } from '../utils'
import { ToastService } from '../toast.service'
import { firstValueFrom } from 'rxjs'

@Component({
  selector: 'app-ongoing-meeting-card',
  standalone: true,
  imports: [
    MatIcon,
    TranslatePipe,
    TranslateDirective,
    MatTooltipModule,
    MatIconButton,
    RouterLink,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatIconButtonSizesModule,
  ],
  templateUrl: './ongoing-meeting-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ongoing-meeting-card.component.scss',
})
export class OngoingMeetingCardComponent {
  @Input({ required: true })
  meeting!: MeetingWithLatestSession

  @Input({ required: true })
  isExpanded!: boolean

  @Input({ required: true })
  useHighlight!: boolean

  @Output()
  menuOpened = new EventEmitter<void>()

  @Output()
  menuClosed = new EventEmitter<void>()

  translateService = inject(TranslateService)
  meetingsService = inject(MeetingsService)
  dialog = inject(MatDialog)
  toastService = inject(ToastService)
  locale = inject(LOCALE_ID)
  switchToNextSubMeetingMutation = this.meetingsService.switchToNextSubMeetingMutation()
  joinNowMutation = this.meetingsService.joinNowMutation()
  extendMeetingMutation = this.meetingsService.extendMeetingMutation()

  pausing = false
  resuming = false
  joining = false

  hangUp(meeting: Meeting) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETINGS.HANG_UP.DIALOG_TITLE'),
        description: this.translateService.instant('MEETINGS.HANG_UP.DIALOG_BODY', { meetingName: meeting.name }),
        descriptionClass: 'mask-meeting-name',
        confirmLabel: this.translateService.instant('COMMON.YES'),
      },
    })
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.meetingsService.hangUpMeeting(meeting.id)
      }
    })
  }

  join(meeting: Meeting) {
    if (meeting.link !== null) {
      openExternalUrl(meeting.link)
    }
  }

  get isRecordingActive(): boolean {
    return (
      this.meeting.latestMeetingSession?.status === MeetingSessionStatus.CONNECTED || this.meeting.latestMeetingSession?.status === MeetingSessionStatus.PAUSED
    )
  }

  get recordingStopsAt(): Date | null {
    return this.isRecordingActive ? addHours(new Date(this.meeting.endDate), 1) : null
  }

  get recordingStopsAtLabel(): string | null {
    return this.recordingStopsAt === null ? null : formatDate(this.recordingStopsAt, 'HH:mm', this.locale)
  }

  get recordingRemaining(): string | null {
    return this.recordingStopsAt === null ? null : formatTimeUntil(this.recordingStopsAt)
  }

  get displayExtendButton(): boolean {
    const belowMaxLength = new Date(this.meeting.endDate) < addHours(new Date(this.meeting.date), 9)
    return this.isRecordingActive && belowMaxLength
  }

  get displayExtendIconButton(): boolean {
    return this.displayExtendButton && new Date() > new Date(this.meeting.endDate)
  }

  async extend(meeting: Meeting): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETINGS.EXTEND_STAY.DIALOG_TITLE'),
        description: this.translateService.instant('MEETINGS.EXTEND_STAY.DIALOG_BODY'),
        confirmLabel: this.translateService.instant('COMMON.YES'),
      },
    })
    const confirmed = await firstValueFrom(dialogRef.afterClosed())
    if (!confirmed) {
      return
    }
    const result = await this.extendMeetingMutation.mutateAsync(meeting.id)
    if (result.status === 'EXTENDED' && result.leaveDate !== null) {
      this.toastService.show('MEETINGS.EXTEND_STAY.SUCCESS_TOAST', { time: formatDate(result.leaveDate, 'd MMM y HH:mm', this.locale) })
    } else if (result.status === 'NOT_EXTENDED') {
      this.toastService.show('MEETINGS.EXTEND_STAY.CARD_AT_MAX')
    } else {
      this.toastService.show('MEETINGS.EXTEND_STAY.ERROR')
    }
  }

  pauseRecording(meeting: Meeting) {
    this.meetingsService.pauseRecording(meeting.id)
    this.pausing = true
  }

  resumeRecording(meeting: Meeting) {
    this.meetingsService.resumeRecording(meeting.id)
    this.resuming = true
  }
  async switchToNextSubMeeting(meeting: Meeting) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETINGS.ONGOING_MEETINGS.PROCEED_TO_NEXT_SUB_MEETING.TITLE'),
        description: this.translateService.instant('MEETINGS.ONGOING_MEETINGS.PROCEED_TO_NEXT_SUB_MEETING.DESCRIPTION'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.switchToNextSubMeetingMutation.mutateAsync(meeting.id)
      }
    })
  }

  get displayStatusButtons(): boolean {
    return !this.pausing && !this.resuming
  }

  get statusMessage(): string | null {
    if (this.meeting.status === MeetingStatus.WAITING_TO_BE_QUEUED) {
      return this.translateService.instant('MEETINGS.ONGOING_MEETINGS.STATUS.QUEUED')
    }
    if (this.meeting.latestMeetingSession === null) {
      return null
    }

    if (this.meeting.latestMeetingSession.status === MeetingSessionStatus.WAITING_TO_START) {
      if (this.joining) {
        return this.translateService.instant('MEETINGS.ONGOING_MEETINGS.STATUS.JOINING')
      }
      const assistantJoinTime = subMinutes(new Date(this.meeting.date), 5)
      const timeUntilJoin = formatTimeUntil(assistantJoinTime)
      if (timeUntilJoin !== null) {
        if (timeUntilJoin === '0m') {
          return this.translateService.instant('MEETINGS.ONGOING_MEETINGS.STATUS.ASSISTANT_JOINS_IN_LESS_THAN_A_MINUTE')
        }
        return this.translateService.instant('MEETINGS.ONGOING_MEETINGS.STATUS.ASSISTANT_JOINS_IN', { time: timeUntilJoin })
      }
      // Join time passed but status hasn't updated yet - the assistant is clicking join
      return this.translateService.instant('MEETINGS.ONGOING_MEETINGS.STATUS.CONNECTING')
    }

    if (this.joining) {
      this.joining = false
    }

    const statusMap = new Map<MeetingSessionStatus, string>([
      [MeetingSessionStatus.QUEUED, 'MEETINGS.ONGOING_MEETINGS.STATUS.QUEUED'],
      [MeetingSessionStatus.CONNECTING, 'MEETINGS.ONGOING_MEETINGS.STATUS.CONNECTING'],
      [MeetingSessionStatus.WAITING_TO_BE_ADMITTED, 'MEETINGS.ONGOING_MEETINGS.STATUS.WAITING_TO_BE_ADMITTED'],
      [MeetingSessionStatus.HANGING_UP, 'MEETINGS.ONGOING_MEETINGS.STATUS.HANGING_UP'],
    ])
    if (statusMap.has(this.meeting.latestMeetingSession.status)) {
      return this.translateService.instant(statusMap.get(this.meeting.latestMeetingSession.status)!)
    }

    if (this.meeting.latestMeetingSession.status === MeetingSessionStatus.CONNECTED) {
      if (this.resuming) {
        this.resuming = false
      } else if (this.pausing) {
        return this.translateService.instant('MEETINGS.ONGOING_MEETINGS.STATUS.PAUSING')
      }
      return this.translateService.instant('MEETINGS.ONGOING_MEETINGS.STATUS.RECORDING')
    }

    if (this.meeting.latestMeetingSession.status === MeetingSessionStatus.PAUSED) {
      if (this.pausing) {
        this.pausing = false
      } else if (this.resuming) {
        return this.translateService.instant('MEETINGS.ONGOING_MEETINGS.STATUS.RESUMING')
      }
      return this.translateService.instant('MEETINGS.ONGOING_MEETINGS.STATUS.PAUSED')
    }

    return null
  }

  get displaySkipToNextSubMeetingButton(): boolean {
    return (
      this.meeting.agendaType === 'MULTIPLE' &&
      this.meeting.latestMeetingSession !== null &&
      (this.meeting.latestMeetingSession.status === MeetingSessionStatus.CONNECTED || this.meeting.latestMeetingSession.status === MeetingSessionStatus.PAUSED)
    )
  }

  get currentParticipantText(): string {
    const count = Math.max(this.meeting.currentParticipantCount! - 1, 0)
    const key = count === 1 ? 'COMMON.ONGOING_PARTICIPANTS_ONE' : 'COMMON.ONGOING_PARTICIPANTS_PLURAL'
    return this.translateService.instant(key, { count })
  }

  get canJoinNow(): boolean {
    if (this.meeting.latestMeetingSession?.status !== MeetingSessionStatus.WAITING_TO_START) {
      return false
    }
    const assistantJoinTime = subMinutes(new Date(this.meeting.date), 5)
    return formatTimeUntil(assistantJoinTime) !== null
  }

  async joinNow(meeting: Meeting): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETINGS.JOIN_IMMEDIATELY.DIALOG_TITLE'),
        description: this.translateService.instant('MEETINGS.JOIN_IMMEDIATELY.DIALOG_DESCRIPTION'),
        confirmLabel: this.translateService.instant('COMMON.YES'),
      },
    })
    const result = await firstValueFrom(dialogRef.afterClosed())
    if (result) {
      this.joining = true
      await this.joinNowMutation.mutateAsync(meeting.id)
      this.toastService.show('MEETINGS.JOIN_IMMEDIATELY.SUCCESS_CONFIRMATION')
    }
  }
}
